import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Divider,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  LinearProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import SwapIcon from '@mui/icons-material/SwapCalls';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { parseInvoiceTable, buildItemsFromTable, matchProduct, fillItemFromMatch, TARGET_FIELDS } from '../../utils/ocrParser';
import { preprocessImageFile } from '../../utils/imagePreprocess';
import { getPdfPages } from '../../utils/pdfToImage';
import { fetchParties, createPurchaseInvoice, fetchAllProducts, getSellerInfo, createParty, createConsignee, fetchConsignees, fetchSavedTransporters, fetchTaxes, fetchUnits } from '../../api';
import SuccessPopup from '../../components/SuccessPopup';

// Helper to fetch a single purchase invoice by id
async function fetchPurchaseInvoiceById(id) {
  const res = await fetch(`/api/purchase-invoices/${id}`);
  if (!res.ok) throw new Error('Failed to fetch invoice');
  return (await res.json()).data;
}

// Helper to update a purchase invoice
async function updatePurchaseInvoice(id, payload) {
  const res = await fetch(`/api/purchase-invoices/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to update invoice');
  return await res.json();
}

export default function Purchase() {
  // Detect edit mode
  const searchParams = new URLSearchParams(window.location.search);
  const editId = searchParams.get('id');
  // Other Charge/Discount state and dialog
  const [otherCharge, setOtherCharge] = useState(0);
  const [otherDiscount, setOtherDiscount] = useState(0);
  const [otherChargeName, setOtherChargeName] = useState('');
  const [otherDiscountName, setOtherDiscountName] = useState('');
  const [otherDialogOpen, setOtherDialogOpen] = useState(false);
  const [otherType, setOtherType] = useState('charge'); // 'charge' or 'discount'
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [signStampImage, setSignStampImage] = useState('');
  const [stampUploading, setStampUploading] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState(() => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  });
  const [message, setMessage] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState('success');
  const [popupTitle, setPopupTitle] = useState('');
  const [popupMessage, setPopupMessage] = useState('');
  const [createdPurchase, setCreatedPurchase] = useState(null);
  const [formType, setFormType] = useState('tax-invoice');
  const [sellerOption, setSellerOption] = useState('show-consignee');
  const [originalForRecipient, setOriginalForRecipient] = useState(false);
  const [duplicateForTransporter, setDuplicateForTransporter] = useState(false);
  const [triplicateForSupplier, setTriplicateForSupplier] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [sellerDialogOpen, setSellerDialogOpen] = useState(false);
  const [addSellerDialogOpen, setAddSellerDialogOpen] = useState(false);
  const [consigneeDialogOpen, setConsigneeDialogOpen] = useState(false);
  const [consigneeSelectDialogOpen, setConsigneeSelectDialogOpen] = useState(false);
  const [consigneeSearch, setConsigneeSearch] = useState('');
  const [consignees, setConsignees] = useState([]);
  const [sellerForm, setSellerForm] = useState({
    name: '', company: '', address: '', city: '', state: '', pincode: '', gstin: '', contact_no: '',
    bank_name: '', ac_holder_name: '', ac_no: '', ifsc_code: '', branch_name: '', upi_id: '', upi_number: ''
  });
  const [transporterDialogOpen, setTransporterDialogOpen] = useState(false);
  const [selectTransporterDialogOpen, setSelectTransporterDialogOpen] = useState(false);
  const [savedTransporters, setSavedTransporters] = useState([]);
  const [optionalFieldsExpanded, setOptionalFieldsExpanded] = useState(false);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [consigneeDetails, setConsigneeDetails] = useState(null);
  const [consigneeForm, setConsigneeForm] = useState({ name: '', address: '', city: '', state: '', pincode: '', gstin: '' });
  const [transportDetails, setTransportDetails] = useState({
    transportation_mode: '',
    lr_number: '',
    lr_date: '',
    vehicle_no: '',
    distance_km: '',
    transporter_name: '',
    transporter_address: '',
    transporter_city: '',
    transporter_state: '',
    transporter_pincode: '',
    transporter_gstin: '',
    transporter_contact_no: '',
  });
  const [otherDetails, setOtherDetails] = useState({
    place_of_supply: '',
    date_of_supply: '',
    supply_type: 'B2B',
    challan_number: '',
    ewaybill_number: '',
    sales_person: '',
    due_date: '',
  });
  const [termsConditions, setTermsConditions] = useState(
    '1. This is an electronically generated document.\n2. All disputes are subject to Gurugram jurisdiction.\n3. If the cheque is dishonoured, a penalty of ₹500 + interest @2% per month on the outstanding amount will be charged.\n4. Payment shall be made within the credit period agreed; a late payment interest of 2% per month shall apply on overdue amounts.\n5. Goods once sold will not be taken back or exchanged, except for manufacturing defect or damaged/wrong delivery reported within 48 hours of delivery.\n6. The seller reserves the right to withhold delivery of further goods if any previous payment is overdue.\n7. Any shortfall/extra delivery, damages or discrepancies must be reported within 48 hours of delivery.\n8. Title of goods passes to the buyer only after full payment is received by the seller.\n9. Prices are subject to change without prior notice unless confirmed by a written quotation/order.\n10. This invoice is valid only if duly signed and stamped by the authorised signatory.'
  );
  const [items, setItems] = useState([
    { id: 1, itemName: '', mrp: '', hsn: '', subtext: '', qty: '', unit: '', price: '', taxType: 'Exclusive', discount: '', discountType: '%', taxableValue: '', gst: '', totalAmt: '' },
    { id: 2, itemName: '', mrp: '', hsn: '', subtext: '', qty: '', unit: '', price: '', taxType: 'Exclusive', discount: '', discountType: '%', taxableValue: '', gst: '', totalAmt: '' },
    { id: 3, itemName: '', mrp: '', hsn: '', subtext: '', qty: '', unit: '', price: '', taxType: 'Exclusive', discount: '', discountType: '%', taxableValue: '', gst: '', totalAmt: '' },
  ]);

  // OCR / Invoice scan state
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scanImagePreview, setScanImagePreview] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanStatus, setScanStatus] = useState('');
  const [scanRawText, setScanRawText] = useState('');
  const [scanTable, setScanTable] = useState(null);
  const [scanMapping, setScanMapping] = useState({});
  const [scanParsedRows, setScanParsedRows] = useState([]);
  const [scanMatched, setScanMatched] = useState(0);
  const [scanError, setScanError] = useState('');
  const [scanDebug, setScanDebug] = useState('');
  const [matchingProducts, setMatchingProducts] = useState(false);
  const [scanEnhance, setScanEnhance] = useState(true);
  const [scanPages, setScanPages] = useState([]);
  const [scanPageIndex, setScanPageIndex] = useState(0);
  const scanFileInputRef = useRef(null);
  const scanFileRef = useRef(null);

  // Product search state for autocomplete
  const [productOptions, setProductOptions] = useState([]);
  const [units, setUnits] = useState([]);
  const [productLoading, setProductLoading] = useState(false);

  // Tax options from settings
  const [taxes, setTaxes] = useState([]);

  // Load taxes from API
  useEffect(() => {
    fetchTaxes()
      .then((res) => {
        const data = res?.data || [];
        setTaxes(data.filter((t) => t.status === 1 || t.status === true));
      })
      .catch(() => setTaxes([]));
  }, []);

  useEffect(() => {
    fetchUnits()
      .then((response) => setUnits(response?.data || []))
      .catch(() => setUnits([]));
  }, []);

  const toNum = useCallback((v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }, []);

  const toLocalDateStr = useCallback((val) => {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d)) return typeof val === 'string' ? val.slice(0, 10) : '';
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  }, []);

  const computeItem = useCallback((item) => {
    const qty = toNum(item.qty);
    const price = toNum(item.price);
    const base = qty * price;
    const discountVal = item.discountType === '₹' ? toNum(item.discount) : (base * toNum(item.discount)) / 100;
    const gstPercent = toNum(item.gst);
    
    // Handle Inclusive vs Exclusive tax type
    if (item.taxType === 'Inclusive') {
      // Price includes GST - deduct GST from the price
      const priceAfterDiscount = Math.max(0, base - discountVal);
      const taxable = priceAfterDiscount / (1 + gstPercent / 100);
      const gstAmount = priceAfterDiscount - taxable;
      return {
        taxableValue: taxable.toFixed(2),
        totalAmt: priceAfterDiscount.toFixed(2),
      };
    } else {
      // Price excludes GST - add GST on top (Exclusive)
      const taxable = Math.max(0, base - discountVal);
      const gstAmount = (taxable * gstPercent) / 100;
      const total = taxable + gstAmount;
      return {
        taxableValue: taxable.toFixed(2),
        totalAmt: total.toFixed(2),
      };
    }
  }, [toNum]);

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const next = prev.map((it) => ({ ...it }));
      next[index][field] = value;
      if (field === 'itemName') {
        next[index].productId = null;
        next[index].productVariantId = null;
      }
      const computed = computeItem(next[index]);
      next[index].taxableValue = computed.taxableValue;
      next[index].totalAmt = computed.totalAmt;
      return next;
    });
  };

  const handleDeleteItem = (id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const handleAddItem = () => {
    const newItem = {
      id: items.length + 1,
      itemName: '',
      mrp: '',
      hsn: '',
      subtext: '',
      qty: '',
      unit: '',
      price: '',
      taxType: 'Exclusive',
      discount: '',
      discountType: '%',
      taxableValue: '',
      gst: '',
      totalAmt: ''
    };
    setItems([...items, newItem]);
  };

  // ---- Invoice image OCR scanning ----

  const openScanDialog = () => {
    setScanError('');
    setScanDebug('');
    setScanRawText('');
    setScanTable(null);
    setScanMapping({});
    setScanParsedRows([]);
    setScanMatched(0);
    setScanImagePreview('');
    setScanDialogOpen(true);
  };

  const closeScanDialog = () => {
    if (scanning) return;
    setScanDialogOpen(false);
  };

  const isPdfFile = (file) => file?.type === 'application/pdf' || /\.pdf$/i.test(file?.name || '');

  const handleScanFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/') && !isPdfFile(file)) {
      setScanError('Please select an image (PNG, JPG) or PDF file');
      return;
    }
    setScanError('');
    scanFileRef.current = file;
    setScanRawText('');
    setScanTable(null);
    setScanMapping({});
    setScanParsedRows([]);
    setScanMatched(0);
    setScanPages([]);
    setScanPageIndex(0);
    setScanning(true);
    setScanProgress(0);
    setScanStatus(isPdfFile(file) ? 'Reading PDF pages...' : 'Initializing OCR engine...');

    try {
      if (isPdfFile(file)) {
        // PDF: extract native text (no OCR) + render preview pages
        const pages = await getPdfPages(file);
        setScanPages(pages);
        if (!pages.length) {
          setScanError('Could not read this PDF. Please try an image instead.');
          setScanning(false);
          return;
        }
        setScanImagePreview(pages[0].dataUrl);
        runOCR(pages); // each page carries its own ocrData
      } else {
        setScanImagePreview(URL.createObjectURL(file));
        runOCR(file);
      }
    } catch (err) {
      console.error('File processing error:', err);
      setScanError('Could not read this file. Try a PDF or image (PNG, JPG).');
      setScanning(false);
    }
  };

  const changeScanPage = (dir) => {
    setScanPageIndex((prev) => {
      const next = Math.max(0, Math.min(scanPages.length - 1, prev + dir));
      if (scanPages[next]) setScanImagePreview(scanPages[next].dataUrl);
      return next;
    });
  };

  const handleEnhanceToggle = (checked) => {
    setScanEnhance(checked);
    // Re-scan the same file with the new enhancement setting
    if (scanFileRef.current && !scanning) {
      setScanError('');
      setScanRawText('');
      setScanTable(null);
      setScanMapping({});
      setScanParsedRows([]);
      setScanMatched(0);
      setScanPages([]);
      setScanPageIndex(0);
      setScanning(true);
      setScanProgress(0);
      setScanStatus('Initializing OCR engine...');
      const file = scanFileRef.current;
      if (isPdfFile(file)) {
        (async () => {
          try {
            const pages = await getPdfPages(file);
            setScanPages(pages);
            if (pages.length) setScanImagePreview(pages[0].dataUrl);
            runOCR(pages);
          } catch (err) {
            setScanError('Could not read this PDF.');
            setScanning(false);
          }
        })();
      } else {
        setScanImagePreview(URL.createObjectURL(file));
        runOCR(file);
      }
    }
  };

  const runOCR = async (input) => {
    try {
      // PDF input = array of { ocrData, dataUrl } pages (text already extracted)
      const isPdfInput = Array.isArray(input) && input.length > 0 && !!input[0]?.ocrData;

      let worker = null;
      let Tesseract = null;
      if (!isPdfInput) {
        // Lazy-load tesseract.js so it does not bloat the main bundle
        Tesseract = await import('tesseract.js');
        worker = await Tesseract.createWorker('eng', 1, {
          logger: (m) => {
            if (m?.status === 'recognizing text') {
              setScanProgress(Math.round((m.progress || 0) * 100));
              setScanStatus('Reading invoice text...');
            } else if (m?.status === 'loading language traineddata') {
              setScanStatus('Loading language model...');
            } else if (m?.status === 'initializing tesseract') {
              setScanStatus('Initializing OCR engine...');
            }
          },
        });
      }

      // Collect per-page OCR data, then run the shared parser on each page
      let allItems = [];
      let detectedTable = null;
      let combinedText = '';
      let totalPages = isPdfInput ? input.length : 1;

      if (isPdfInput) {
        for (let idx = 0; idx < input.length; idx++) {
          setScanStatus(`Reading PDF text (page ${idx + 1}/${input.length})...`);
          const { ocrData, dataUrl } = input[idx];
          combinedText += (ocrData?.text || '') + '\n';
          const table = parseInvoiceTable(ocrData || {});
          if (!detectedTable && table.columns?.length) detectedTable = table;
          const pageItems = table.mode === 'table'
            ? buildItemsFromTable(table, table.mapping)
            : (table.items || []);
          if (pageItems.length) allItems.push(...pageItems);
          if (idx === 0 && dataUrl) setScanImagePreview(dataUrl);
        }
      } else {
        const images = Array.isArray(input) ? input : [input];
        for (let idx = 0; idx < images.length; idx++) {
          const pageBlob = images[idx];
          const pageLabel = images.length > 1 ? ` (page ${idx + 1}/${images.length})` : '';

          let imageInput = pageBlob;
          if (scanEnhance) {
            setScanStatus(`Enhancing image${pageLabel}...`);
            try {
              const processed = await preprocessImageFile(pageBlob, { binarize: false });
              if (idx === 0) setScanImagePreview(processed.dataUrl);
              imageInput = processed.blob;
            } catch (pe) {
              console.error('Image enhance failed, using original:', pe);
              imageInput = pageBlob;
            }
          }

          setScanStatus(`Extracting text${pageLabel}...`);
          const { data } = await worker.recognize(imageInput);
          combinedText += (data?.text || '') + '\n';
          const table = parseInvoiceTable(data);
          if (!detectedTable && table.columns?.length) detectedTable = table;
          const pageItems = table.mode === 'table'
            ? buildItemsFromTable(table, table.mapping)
            : (table.items || []);
          if (pageItems.length) allItems.push(...pageItems);
        }
      }

      if (worker) await worker.terminate();

      // Deduplicate merged items across pages
      const seen = new Set();
      const mappedRows = [];
      for (const it of allItems) {
        const key = `${it.itemName}|${it.qty}|${it.price}|${it.totalAmt}`;
        if (seen.has(key)) continue;
        seen.add(key);
        mappedRows.push(it);
      }

      setScanTable(detectedTable || (mappedRows.length ? { columns: [], rows: [], mapping: {}, mode: 'content' } : null));
      setScanMapping(detectedTable?.mapping || {});
      setScanRawText(combinedText.trim());
      setScanProgress(100);
      setScanStatus('Detecting table columns...');

      setScanDebug(
        `pages: ${totalPages} | mode: ${detectedTable ? detectedTable.mode : 'content'} | columns: ${detectedTable?.columns?.length || 0} | rows: ${detectedTable?.rows?.length || 0} | items: ${mappedRows.length} | text chars: ${combinedText.length}`
      );

      setScanParsedRows(mappedRows);

      if (!mappedRows.length) {
        setScanError('Could not detect any item rows. Try a clearer, higher-resolution image.');
        setScanning(false);
        return;
      }

      // Match against existing products
      setMatchingProducts(true);
      setScanStatus('Matching products...');
      const all = await fetchAllProducts();
      const products = all?.data || [];
      let matchedCount = 0;
      const enriched = mappedRows.map((r) => {
        const match = matchProduct(r, products);
        if (match) matchedCount++;
        return { ...r, match };
      });
      setScanMatched(matchedCount);
      setScanParsedRows(enriched);
      setMatchingProducts(false);
      setScanning(false);
      setScanStatus('Done');
    } catch (err) {
      console.error('OCR scan error:', err);
      setScanError('OCR failed. Please try again with a clearer image.');
      setScanning(false);
      setScanStatus('');
    }
  };

  const updateScanRow = (index, field, value) => {
    setScanParsedRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleMappingChange = async (colId, target) => {
    const next = { ...scanMapping, [colId]: target };
    setScanMapping(next);
    if (!scanTable || scanTable.mode !== 'table') return;
    const mappedRows = buildItemsFromTable(scanTable, next);
    const all = await fetchAllProducts();
    const products = all?.data || [];
    let matchedCount = 0;
    const enriched = mappedRows.map((r) => {
      const match = matchProduct(r, products);
      if (match) matchedCount++;
      return { ...r, match };
    });
    setScanMatched(matchedCount);
    setScanParsedRows(enriched);
  };

  const applyScanResults = () => {
    const rows = scanParsedRows.filter((r) => r.itemName || r.price || r.totalAmt);
    if (!rows.length) return;

    // Start with the parsed rows, keep the max of current count to preserve ids
    const baseId = items.length;
    const newRows = rows.map((r, i) => {
      const filled = fillItemFromMatch(r, r.match);
      return {
        id: baseId + i + 1,
        itemName: filled.itemName,
        mrp: filled.mrp,
        hsn: filled.hsn,
        subtext: '',
        qty: filled.qty,
        unit: filled.unit,
        price: filled.price,
        taxType: 'Exclusive',
        discount: filled.discount,
        discountType: '%',
        taxableValue: '',
        gst: filled.gst,
        totalAmt: '',
        productId: filled.productId,
        productVariantId: filled.productVariantId,
      };
    });

    // Compute taxable/total for each new row
    const computedRows = newRows.map((it) => {
      const computed = computeItem(it);
      return { ...it, taxableValue: computed.taxableValue, totalAmt: computed.totalAmt };
    });

    setItems((prev) => {
      // Keep rows that are empty placeholders out of the way: replace blanks if present, else append
      const blanks = prev.filter(
        (it) => !it.itemName && !it.price && !it.qty && !it.hsn
      );
      if (blanks.length >= computedRows.length) {
        // Fill the first N blank rows
        const next = prev.map((it) => {
          if (blanks.includes(it)) {
            const row = computedRows[blanks.indexOf(it)];
            return row ? { ...it, ...row, id: it.id } : it;
          }
          return it;
        });
        return next;
      }
      return [...prev, ...computedRows];
    });

    setScanDialogOpen(false);
    setScanImagePreview('');
    setScanRawText('');
    setScanTable(null);
    setScanMapping({});
    setScanParsedRows([]);
    setScanMatched(0);
  };

  // Search products for autocomplete
  const handleProductSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setProductOptions([]);
      return;
    }
    
    setProductLoading(true);
    try {
      const res = await fetchAllProducts(searchQuery.trim());
      if (res?.ok && res?.data) {
        // Flatten products with their variants into selectable options
        const options = [];
        res.data.forEach(product => {
          if (product.variants && product.variants.length > 0) {
            product.variants.forEach(variant => {
              options.push({
                label: !variant.variant_name || variant.variant_name === 'Default'
                  ? product.name
                  : `${product.name} - ${variant.variant_name}${variant.unit_name ? ` (${variant.unit_name})` : ''}`,
                product,
                variant,
              });
            });
          } else {
            // Product without variants
            options.push({
              label: product.name,
              product,
              variant: null,
            });
          }
        });
        setProductOptions(options);
      } else {
        setProductOptions([]);
      }
    } catch (err) {
      console.error('Product search error:', err);
      setProductOptions([]);
    } finally {
      setProductLoading(false);
    }
  }, []);

  // Handle product selection from autocomplete
  const handleProductSelect = useCallback((index, option) => {
    if (!option) return;
    
    const { product, variant } = option;
    
    setItems((prev) => {
      const next = prev.map((it) => ({ ...it }));
      const item = next[index];
      
      // Auto-fill fields from product/variant
      item.itemName = product.name || '';
      item.productId = product.id || null;
      item.productVariantId = variant?.id || null;
      // price column = MRP, discounted_price column = sale price
      item.mrp = variant?.price || product.price || '';
      item.price = variant?.discounted_price || variant?.price || product.price || '';
      item.hsn = variant?.hsn || product.hsn || '';
      item.unit = variant?.unit_name || '';
      
      // Keep existing values for other fields or set defaults
      if (!item.qty) item.qty = '1';
      
      // Set GST from product's configured tax percentage if available
      // Product has tax_id and tax_percentage from the database
      if (!item.gst && product.tax_percentage !== undefined) {
        item.gst = String(product.tax_percentage);
      } else if (!item.gst) {
        // Default to 18 if no product tax config found
        item.gst = '18';
      }
      
      // Recompute totals
      const computed = computeItem(item);
      item.taxableValue = computed.taxableValue;
      item.totalAmt = computed.totalAmt;
      
      return next;
    });
  }, [computeItem]);

  // Load consignees when opening the select dialog or when search changes
  const openConsigneeSelect = useCallback(async () => {
    setConsigneeSelectDialogOpen(true);
    setConsigneeSearch('');
    try {
      const data = await fetchConsignees('');
      setConsignees(data?.items || []);
    } catch (e) {
      console.error('fetch consignees error', e);
      setConsignees([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!consigneeSelectDialogOpen) return;
      try {
        const data = await fetchConsignees(consigneeSearch);
        if (!cancelled) setConsignees(data?.items || []);
      } catch (e) {
        if (!cancelled) setConsignees([]);
      }
    })();
    return () => { cancelled = true; };
  }, [consigneeSelectDialogOpen, consigneeSearch]);

  const totals = useMemo(() => {
    const base = items.reduce(
      (acc, item) => {
        const { taxableValue, totalAmt } = computeItem(item);
        const taxable = toNum(taxableValue);
        const total = toNum(totalAmt);
        const gst = total - taxable;
        return {
          taxable: acc.taxable + taxable,
          gst: acc.gst + gst,
          total: acc.total + total,
        };
      },
      { taxable: 0, gst: 0, total: 0 }
    );
    // Calculate final amount with otherCharge and otherDiscount
    const final = base.total + (Number(otherCharge) || 0) - (Number(otherDiscount) || 0);
    return { ...base, final };
  }, [items, computeItem, toNum, otherCharge, otherDiscount]);

  const filteredVendors = useMemo(() => {
    if (!vendorSearch.trim()) return vendors;
    const term = vendorSearch.toLowerCase();
    return vendors.filter(
      (v) =>
        v.name?.toLowerCase().includes(term) ||
        v.company?.toLowerCase().includes(term) ||
        v.gstin?.toLowerCase().includes(term)
    );
  }, [vendors, vendorSearch]);


  useEffect(() => {
    const loadVendors = async () => {
      try {
        const res = await fetchParties('vendor');
        setVendors(res.data || []);
      } catch (err) {
        console.error('fetch vendors error', err);
      }
    };
    loadVendors();
  }, []);

  // Edit mode: fetch and prefill invoice
  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPurchaseInvoiceById(editId);
        if (!data || cancelled) return;
        const { invoice, items: invItems, extra, consignee } = data;
        setInvoiceNo(invoice.invoice_no || '');
        setPurchaseDate(toLocalDateStr(invoice.purchase_date));
        setOtherCharge(invoice.other_charge || 0);
        setOtherDiscount(invoice.other_discount || 0);
        setOtherChargeName(invoice.other_charge_name || '');
        setOtherDiscountName(invoice.other_discount_name || '');
        setTermsConditions(invoice.terms_conditions || '');
        setSignStampImage(invoice.sign_stamp_image || '');
        setSelectedSeller({
          id: invoice.party_id,
          name: invoice.party_name,
          company: invoice.party_company,
          gstin: invoice.party_gstin,
          address: invoice.party_address ?? invoice.address,
          city: invoice.party_city ?? invoice.city,
          state: invoice.party_state ?? invoice.state,
          pincode: invoice.party_pincode ?? invoice.pincode,
        });
        if (consignee) {
          setConsigneeDetails({
            id: consignee.id,
            name: consignee.name,
            company: consignee.company,
            address: consignee.address,
            city: consignee.city,
            state: consignee.state,
            pincode: consignee.pincode,
            gstin: consignee.gstin,
          });
          const consigneeName = String(consignee.name || '');
          const partyName = String(invoice.party_name || '');
          if (consigneeName && consigneeName !== partyName) {
            setSellerOption('add-consignee');
          }
        } else {
          setConsigneeDetails(null);
        }
        setItems(
          (invItems || []).map((it, idx) => ({
            id: idx + 1,
            itemName: it.item_name,
            productId: it.product_id || null,
            productVariantId: it.product_variant_id || null,
            mrp: it.mrp,
            hsn: it.hsn,
            subtext: it.subtext,
            qty: it.qty,
            unit: it.unit,
            price: it.price,
            taxType: it.tax_type,
            discount: it.discount,
            discountType: it.discount_type,
            taxableValue: it.taxable_value,
            gst: it.gst_percent != null && it.gst_percent !== '' ? String(Number(it.gst_percent)) : '',
            totalAmt: it.total_amt,
          }))
        );
        if (extra) {
          setTransportDetails((prev) => ({ ...prev, ...extra }));
          setOtherDetails((prev) => ({ ...prev, ...extra }));
        }
        // Restore copy type checkboxes
        setOriginalForRecipient(!!invoice.original_for_recipient);
        setDuplicateForTransporter(!!invoice.duplicate_for_transporter);
        setTriplicateForSupplier(!!invoice.triplicate_for_supplier);
        // Restore invoice type (tax invoice / bill of supply)
        setFormType(invoice.invoice_type === 'bill-of-supply' ? 'bill-of-supply' : 'tax-invoice');
      } catch (err) {
        setMessage('Failed to load invoice');
      }
    })();
    return () => { cancelled = true; };
  }, [editId]);

  useEffect(() => {
    const loadSeller = async () => {
      try {
        const res = await getSellerInfo();
        setSellerInfo(res?.data || null);
      } catch (err) {
        console.error('fetch seller error', err);
      }
    };
    loadSeller();
  }, []);

  // Prefill transporter details from selected seller when available
  useEffect(() => {
    if (!selectedSeller) return;
    setTransportDetails((prev) => ({
      ...prev,
      transporter_name: prev.transporter_name || selectedSeller?.name || selectedSeller?.company || '',
      transporter_address: prev.transporter_address || selectedSeller?.address || '',
      transporter_city: prev.transporter_city || selectedSeller?.city || '',
      transporter_state: prev.transporter_state || selectedSeller?.state || '',
      transporter_pincode: prev.transporter_pincode || selectedSeller?.pincode || '',
      transporter_gstin: prev.transporter_gstin || selectedSeller?.gstin || '',
      transporter_contact_no: prev.transporter_contact_no || selectedSeller?.contact_no || '',
    }));
  }, [selectedSeller]);

  // Load saved transporters when select dialog opens
  useEffect(() => {
    const loadSavedTransporters = async () => {
      if (!selectTransporterDialogOpen) return;
      try {
        const data = await fetchSavedTransporters();
        setSavedTransporters(data?.items || []);
      } catch (err) {
        console.error('fetch saved transporters error', err);
        setSavedTransporters([]);
      }
    };
    loadSavedTransporters();
  }, [selectTransporterDialogOpen]);

  const handleSelectTransporter = (transporter) => {
    setTransportDetails((prev) => ({
      ...prev,
      transporter_name: transporter.transporter_name || '',
      transporter_address: transporter.transporter_address || '',
      transporter_city: transporter.transporter_city || '',
      transporter_state: transporter.transporter_state || '',
      transporter_pincode: transporter.transporter_pincode || '',
      transporter_gstin: transporter.transporter_gstin || '',
      transporter_contact_no: transporter.transporter_contact_no || '',
    }));
    setSelectTransporterDialogOpen(false);
  };

  const resetForm = () => {
    setInvoiceNo('');
    setSelectedSeller(null);
    setPurchaseDate('');
    setItems([
      { id: 1, itemName: '', mrp: '', hsn: '', subtext: '', qty: '', unit: '', price: '', taxType: 'Exclusive', discount: '', discountType: '%', taxableValue: '', gst: '', totalAmt: '' },
      { id: 2, itemName: '', mrp: '', hsn: '', subtext: '', qty: '', unit: '', price: '', taxType: 'Exclusive', discount: '', discountType: '%', taxableValue: '', gst: '', totalAmt: '' },
      { id: 3, itemName: '', mrp: '', hsn: '', subtext: '', qty: '', unit: '', price: '', taxType: 'Exclusive', discount: '', discountType: '%', taxableValue: '', gst: '', totalAmt: '' },
    ]);
    setOtherDetails({
      place_of_supply: '',
      date_of_supply: '',
      supply_type: 'B2B',
      challan_number: '',
      ewaybill_number: '',
      sales_person: '',
      due_date: '',
    });
    setTransportDetails({
      transportation_mode: '',
      lr_number: '',
      lr_date: '',
      vehicle_no: '',
      distance_km: '',
      transporter_name: '',
      transporter_address: '',
      transporter_city: '',
      transporter_state: '',
      transporter_pincode: '',
      transporter_gstin: '',
      transporter_contact_no: '',
    });
    setConsigneeDetails(null);
    setConsigneeForm({ name: '', address: '', city: '', state: '', pincode: '', gstin: '' });
    setOtherCharge(0);
    setOtherDiscount(0);
    setOtherChargeName('');
    setOtherDiscountName('');
    setSignStampImage('');
    setSellerOption('');
    setMessage('');
  };

  const handleSubmit = async () => {
    if (!invoiceNo.trim()) {
      setMessage('Invoice number is required');
      return;
    }
    if (!selectedSeller?.id) {
      setMessage('Select a vendor');
      return;
    }
    const hasTaxApplied = items.some((it) => it.itemName && String(it.itemName).trim() && toNum(it.gst) > 0);
    const invoiceType = formType === 'bill-of-supply' ? 'bill-of-supply' : (hasTaxApplied ? 'tax-invoice' : 'general-invoice');
    const payload = {
      invoice_no: invoiceNo.trim(),
      invoice_type: invoiceType,
      party_id: selectedSeller.id,
      purchase_date: purchaseDate || null,
      sign_stamp_image: signStampImage || null,
      taxable_amount: Number(totals.taxable.toFixed(2)),
      gst_amount: Number(totals.gst.toFixed(2)),
      total_amount: Number(totals.total.toFixed(2)),
      final_amount: Number(totals.final.toFixed(2)),
      other_charge: Number(otherCharge) || 0,
      other_discount: Number(otherDiscount) || 0,
      other_charge_name: otherChargeName,
      other_discount_name: otherDiscountName,
      original_for_recipient: originalForRecipient,
      duplicate_for_transporter: duplicateForTransporter,
      triplicate_for_supplier: triplicateForSupplier,
      consignee_id: consigneeDetails?.id || null,
      terms_conditions: termsConditions,
      items: items
        .filter((it) => it.itemName.trim())
        .map((it) => ({
          item_name: it.itemName,
          product_id: it.productId || null,
          product_variant_id: it.productVariantId || null,
          mrp: toNum(it.mrp) || null,
          hsn: it.hsn || null,
          subtext: it.subtext || null,
          qty: toNum(it.qty) || null,
          unit: it.unit || null,
          price: toNum(it.price) || null,
          tax_type: it.taxType || 'Exclusive',
          discount: toNum(it.discount) || null,
          discount_type: it.discountType || '%',
          taxable_value: toNum(it.taxableValue) || null,
          gst_percent: toNum(it.gst) || null,
          total_amt: toNum(it.totalAmt) || null,
        })),
      extra: {
        ...transportDetails,
        ...otherDetails,
        consignee:
          sellerOption === 'add-consignee'
            ? consigneeDetails
            : sellerOption === 'show-consignee'
            ? {
                id: null,
                name: sellerInfo?.store_name || 'STORE NAME',
                address: sellerInfo?.street || '',
                city: sellerInfo?.city_name || '',
                state: sellerInfo?.state || '',
                pincode: sellerInfo?.pincode_value || '',
                gstin: sellerInfo?.tax_number || '',
              }
            : null,
      },
    };

    setLoading(true);
    setMessage('');
    setCreatedPurchase(null);
    try {
      if (editId) {
        await updatePurchaseInvoice(editId, payload);
        setPopupType('success');
        setPopupTitle('Success');
        setPopupMessage('Invoice updated successfully');
        setShowPopup(true);
      } else {
        const created = await createPurchaseInvoice(payload);
        setCreatedPurchase(created);
        setPopupType('success');
        setPopupTitle('Success');
        setPopupMessage('Invoice created successfully');
        setShowPopup(true);
        resetForm();
      }
    } catch (err) {
      setPopupType('error');
      setPopupTitle('Error');
      setPopupMessage(err?.response?.data?.error || err.message || 'Save failed');
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const handleStampUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 2 * 1024 * 1024) {
      setMessage('Upload a PNG, JPEG, or WebP image up to 2 MB.');
      return;
    }

    setStampUploading(true);
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('stamp', file);
      const response = await fetch('/api/uploads/stamp', { method: 'POST', body: formData });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Stamp upload failed');
      setSignStampImage(result.url);
    } catch (error) {
      setMessage(error.message || 'Stamp upload failed');
    } finally {
      setStampUploading(false);
    }
  };

  return (
    <Box sx={{ p: 1, bgcolor: '#f5f5f5', minHeight: '100vh', width: '100%', maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <IconButton onClick={() => navigate('/accounts/invoices?tab=0')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {editId ? 'Edit Purchase' : 'Create Purchase'}
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="outlined" size="small" onClick={() => navigate('/accounts/invoices?tab=0')}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" size="small" disabled={loading} onClick={handleSubmit}>
            {loading ? (editId ? 'Updating...' : 'Saving...') : (editId ? 'Update' : 'Submit')}
          </Button>
        </Box>
      </Box>

      {message && (
        <Typography variant="caption" sx={{ color: '#2e7d32', ml: 1, mb: 1, display: 'block' }}>
          {message}
        </Typography>
      )}

      <SuccessPopup
        open={showPopup}
        setOpen={setShowPopup}
        type={popupType}
        title={popupTitle}
        message={popupMessage}
        onCreateNew={() => { window.location.href = '/accounts/purchase'; }}
        onPrint={() => { const printId = editId || createdPurchase?.id; if (printId) window.open(`/print-invoice/${printId}?type=purchase`, '_blank'); }}
        onSeeInvoices={() => navigate('/accounts/Invoices')}
        isPurchase={true}
      />

      <Grid
        container
        spacing={1}
        sx={{ flexWrap: 'nowrap', overflowX: 'auto', alignItems: 'stretch', pb: 1 }}
      >
        {/* Invoice Details Column */}
        <Grid item xs={12} md={3} sx={{ flex: '1 0 280px', maxWidth: 'none' }}>
          <Paper sx={{ p: 1.5, height: '100%', width: '100%', minWidth: 0, bgcolor: '#e0f2f1', borderTop: '4px solid #00897b' }}>
            {/* Form Type Selection */}
            <Box sx={{ mb: 1.5 }}>
              <RadioGroup
                row
                value={formType}
                onChange={(e) => setFormType(e.target.value)}
                sx={{ gap: 0.5 }}
              >
                <FormControlLabel
                  value="tax-invoice"
                  control={<Radio size="small" />}
                  label={<Typography variant="caption">Tax Invoice</Typography>}
                />
                <FormControlLabel
                  value="bill-of-supply"
                  control={<Radio size="small" />}
                  label={<Typography variant="caption">Bill of Supply</Typography>}
                />
              </RadioGroup>
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            <Grid container spacing={1}>
              {/* Purchase Date */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Typography variant="caption" sx={{ color: '#666', flex: '0 0 86px' }}>
                  Purchase Date
                </Typography>
                <TextField
                  type="date"
                  value={purchaseDate}
                  size="small"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  inputProps={{ style: { fontSize: '0.85rem', height: 24, padding: '4px 8px' } }}
                  sx={{ flex: 1, minWidth: 0, '& .MuiInputBase-input': { py: 0.5, px: 1, fontSize: '0.85rem', height: 24 } }}
                />
                </Box>
              </Grid>

              {/* Invoice Purchase No. */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Typography variant="caption" sx={{ color: '#666', flex: '0 0 138px' }}>
                  Purchase Invoice No.
                </Typography>
                <TextField
                  value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value)}
                  size="small"
                  variant="outlined"
                  inputProps={{ style: { fontSize: '0.85rem', height: 24, padding: '4px 8px' } }}
                  sx={{ flex: 1, minWidth: 0, '& .MuiInputBase-input': { py: 0.5, px: 1, fontSize: '0.85rem', height: 24 } }}
                />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Buyer Details Column */}
        <Grid item xs={12} md={3} sx={{ flex: '1 0 280px', maxWidth: 'none' }}>
          <Paper
            sx={{
              p: 1.5,
              height: '100%',
              width: '100%',
              minWidth: 0,
              bgcolor: '#e8eaf6',
              borderTop: '4px solid #5c6bc0',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                BUYER DETAILS
              </Typography>
              <IconButton size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              {sellerInfo?.store_name || 'STORE NAME'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
              {sellerInfo?.formatted_address || sellerInfo?.street || 'Street address'}
            </Typography>

            {sellerInfo?.formatted_address ? (
              <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                GSTIN: {sellerInfo?.tax_number || '-'}
              </Typography>
            ) : (
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    City:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                    {sellerInfo?.city_name || 'City'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    State:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                    {sellerInfo?.state || 'State'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Pincode:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                    {sellerInfo?.pincode_value || '000000'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    GSTIN:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', fontSize: '0.7rem' }}>
                    {sellerInfo?.tax_number || '-'}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>

        {/* Seller Details Column */}
        <Grid item xs={12} md={3} sx={{ flex: '1 0 280px', maxWidth: 'none' }}>
          <Paper sx={{ p: 1.5, height: '100%', width: '100%', minWidth: 0, bgcolor: '#f3e5f5', borderTop: '4px solid #7b1fa2' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                SELLER DETAILS
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton size="small" color="primary" onClick={() => setSellerDialogOpen(true)} title="Select Seller">
                  <PersonSearchIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="primary" title="Add New Seller" onClick={() => setAddSellerDialogOpen(true)}>
                  <PersonAddIcon fontSize="small" />
                </IconButton>
                    {/* Add Seller Dialog */}
                    <Dialog
                      open={addSellerDialogOpen}
                      onClose={() => setAddSellerDialogOpen(false)}
                      maxWidth="sm"
                      fullWidth
                    >
                      <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          Add New Seller (Vendor)
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField fullWidth label="Name" size="small" value={sellerForm.name} onChange={(e)=>setSellerForm(f=>({...f,name:e.target.value}))} />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth label="Company" size="small" value={sellerForm.company} onChange={(e)=>setSellerForm(f=>({...f,company:e.target.value}))} />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth label="Address" size="small" multiline rows={2} value={sellerForm.address} onChange={(e)=>setSellerForm(f=>({...f,address:e.target.value}))} />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField fullWidth label="City" size="small" value={sellerForm.city} onChange={(e)=>setSellerForm(f=>({...f,city:e.target.value}))} />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField fullWidth label="State" size="small" value={sellerForm.state} onChange={(e)=>setSellerForm(f=>({...f,state:e.target.value}))} />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField fullWidth label="Pincode" size="small" value={sellerForm.pincode} onChange={(e)=>setSellerForm(f=>({...f,pincode:e.target.value}))} />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField fullWidth label="GSTIN" size="small" value={sellerForm.gstin} onChange={(e)=>setSellerForm(f=>({...f,gstin:e.target.value}))} />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth label="Contact No" size="small" value={sellerForm.contact_no} onChange={(e)=>setSellerForm(f=>({...f,contact_no:e.target.value}))} />
                          </Grid>
                          <Grid item xs={12}>
                            <Divider textAlign="left">Bank Details</Divider>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth label="Bank Name" size="small" value={sellerForm.bank_name} onChange={(e)=>setSellerForm(f=>({...f,bank_name:e.target.value}))} />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth label="A/C Holder Name" size="small" value={sellerForm.ac_holder_name} onChange={(e)=>setSellerForm(f=>({...f,ac_holder_name:e.target.value}))} />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField fullWidth label="A/C No." size="small" value={sellerForm.ac_no} onChange={(e)=>setSellerForm(f=>({...f,ac_no:e.target.value}))} />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField fullWidth label="IFSC Code" size="small" value={sellerForm.ifsc_code} onChange={(e)=>setSellerForm(f=>({...f,ifsc_code:e.target.value}))} />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth label="Branch Name" size="small" value={sellerForm.branch_name} onChange={(e)=>setSellerForm(f=>({...f,branch_name:e.target.value}))} />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField fullWidth label="UPI ID" size="small" value={sellerForm.upi_id} onChange={(e)=>setSellerForm(f=>({...f,upi_id:e.target.value}))} />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField fullWidth label="UPI Number" size="small" value={sellerForm.upi_number} onChange={(e)=>setSellerForm(f=>({...f,upi_number:e.target.value}))} />
                          </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
                          <Button variant="outlined" size="small" onClick={()=>setAddSellerDialogOpen(false)}>Cancel</Button>
                          <Button variant="contained" color="primary" size="small" onClick={async ()=>{
                            try {
                              if (!sellerForm.name.trim()) return;
                              const payload = { ...sellerForm, is_vendor: true };
                              const res = await createParty(payload);
                              // Refresh list and set selected
                              const list = await fetchParties('vendor');
                              setVendors(list.data || []);
                              const created = list.data?.find(p=>p.id === res.id) || null;
                              if (created) setSelectedSeller(created);
                              setAddSellerDialogOpen(false);
                            } catch(err){
                              console.error('create vendor error', err);
                            }
                          }}>Save</Button>
                        </Box>
                      </Box>
                    </Dialog>
              </Box>
            </Box>

            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              {selectedSeller?.name || 'VENDOR NAME'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
              {selectedSeller?.address || 'Address: Please select a seller'}
            </Typography>

            <Grid container spacing={0.5}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  City:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                  {selectedSeller?.city || 'City Name'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  State:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                  {selectedSeller?.state || 'State Name'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Pincode:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                  {selectedSeller?.pincode || '000000'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  GSTIN:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', fontSize: '0.7rem' }}>
                  {selectedSeller?.gstin || '-'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Consignee Details Column */}
        <Grid item xs={12} md={3} sx={{ flex: '1 0 280px', maxWidth: 'none' }}>
          <Paper sx={{ p: 1.5, height: '100%', width: '100%', minWidth: 0, bgcolor: '#fff3e0', borderTop: '4px solid #f57c00' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                CONSIGNEE DETAILS
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" color="primary" title="Select Consignee" onClick={openConsigneeSelect}>
                  <SearchIcon fontSize="small" />
                </IconButton>
                <Select
                  value={sellerOption}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSellerOption(value);
                    if (value === 'add-consignee') {
                      setConsigneeDialogOpen(true);
                    }
                  }}
                  size="small"
                  sx={{ minWidth: 140, height: 28, fontSize: '0.75rem' }}
                >
                  <MenuItem value="show-consignee">Same as Buyer</MenuItem>
                  <MenuItem value="same-as-seller">Same as Seller</MenuItem>
                  <MenuItem value="not-required">Not Required</MenuItem>
                  <MenuItem value="add-consignee">Different Address</MenuItem>
                </Select>
              </Box>
            </Box>
            {sellerOption === 'show-consignee' && sellerInfo && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  {sellerInfo?.store_name || 'STORE NAME'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                  {sellerInfo?.formatted_address || sellerInfo?.street || 'Street address'}
                </Typography>
                {sellerInfo?.formatted_address ? (
                  <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                    GSTIN: {sellerInfo?.tax_number || '-'}
                  </Typography>
                ) : (
                  <Grid container spacing={0.5}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        City:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                        {sellerInfo?.city_name || 'City'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        State:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                        {sellerInfo?.state || 'State'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Pincode:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                        {sellerInfo?.pincode_value || '000000'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        GSTIN:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', fontSize: '0.7rem' }}>
                        {sellerInfo?.tax_number || '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </>
            )}

            {sellerOption === 'same-as-seller' && selectedSeller && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  {selectedSeller?.name || selectedSeller?.company || 'VENDOR NAME'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                  {selectedSeller?.address || 'Vendor Address Details'}
                </Typography>

                <Grid container spacing={0.5}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      City:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                      {selectedSeller?.city || 'City Name'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      State:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                      {selectedSeller?.state || 'State Name'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Pincode:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                      {selectedSeller?.pincode || '000000'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      GSTIN:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', fontSize: '0.7rem' }}>
                      {selectedSeller?.gstin || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}

            {sellerOption === 'add-consignee' && consigneeDetails && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  {consigneeDetails?.name || 'CONSIGNEE NAME'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                  {consigneeDetails?.address || 'Consignee Address Details'}
                </Typography>

                <Grid container spacing={0.5}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      City:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                      {consigneeDetails?.city || 'City Name'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      State:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                      {consigneeDetails?.state || 'State Name'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Pincode:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                      {consigneeDetails?.pincode || '000000'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      GSTIN:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', fontSize: '0.7rem' }}>
                      {consigneeDetails?.gstin || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Products */}
      <Paper sx={{ p: 1.5, mt: 2, bgcolor: '#e8e8e8' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="span" sx={{ fontSize: '1.2rem' }}>📦</Box>
            ITEM DETAILS
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<DocumentScannerIcon />}
              sx={{ height: 30 }}
              onClick={openScanDialog}
            >
              Scan Invoice
            </Button>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{ height: 30 }} onClick={handleAddItem}>
              Add New Item
            </Button>
          </Box>
        </Box>
        
        <TableContainer sx={{ bgcolor: '#fff', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ width: 40, p: 0.5 }}>#</TableCell>
                <TableCell sx={{ minWidth: 180, p: 0.5 }}>Item Name</TableCell>
                <TableCell sx={{ width: 80, p: 0.5 }}>Mrp</TableCell>
                <TableCell sx={{ width: 80, p: 0.5 }}>HSN</TableCell>
                <TableCell sx={{ width: 60, p: 0.5 }}>Qty</TableCell>
                <TableCell sx={{ width: 80, p: 0.5 }}>Unit</TableCell>
                <TableCell sx={{ width: 80, p: 0.5 }}>Price</TableCell>
                <TableCell sx={{ width: 100, p: 0.5 }}>Tax Type</TableCell>
                <TableCell sx={{ width: 100, p: 0.5 }}>Discount</TableCell>
                <TableCell sx={{ width: 100, p: 0.5 }}>Taxable Value</TableCell>
                <TableCell sx={{ width: 80, p: 0.5 }}>GST</TableCell>
                <TableCell sx={{ width: 100, p: 0.5 }}>Total Amt</TableCell>
                <TableCell sx={{ width: 40, p: 0.5 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell sx={{ p: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DragIndicatorIcon fontSize="small" sx={{ color: '#999', cursor: 'move' }} />
                      {index + 1}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <Autocomplete
                      freeSolo
                      fullWidth
                      size="small"
                      options={productOptions}
                      loading={productLoading}
                      value={item.itemName}
                      onInputChange={(event, newValue) => {
                        handleItemChange(index, 'itemName', newValue);
                        handleProductSearch(newValue);
                      }}
                      onChange={(event, newValue) => {
                        if (typeof newValue === 'object' && newValue !== null) {
                          handleProductSelect(index, newValue);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.defaultMuiPrevented) {
                          const typed = String(item.itemName || '').trim().toLowerCase();
                          if (typed && productOptions.length) {
                            const exact = productOptions.find((opt) => opt.label.toLowerCase() === typed);
                            handleProductSelect(index, exact || productOptions[0]);
                          }
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search or Enter Item Name"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {productLoading ? <Typography variant="caption" sx={{ mr: 1 }}>...</Typography> : null}
                                {params.InputProps.endAdornment}
                                <IconButton size="small" sx={{ p: 0.5 }}>
                                  <SearchIcon fontSize="small" sx={{ color: '#999' }} />
                                </IconButton>
                              </>
                            ),
                            sx: { height: 30 }
                          }}
                        />
                      )}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      value={item.subtext}
                      placeholder="Subtext (e.g. Mfg/Expiry details)"
                      InputProps={{ sx: { height: 24, fontSize: '0.7rem', mt: 0.5 } }}
                      onChange={(e) => handleItemChange(index, 'subtext', e.target.value)}
                    />
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <TextField fullWidth size="small" value={item.mrp} placeholder="-" InputProps={{ sx: { height: 24, fontSize: '0.75rem' } }} onChange={(e) => handleItemChange(index, 'mrp', e.target.value)} />
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <TextField fullWidth size="small" value={item.hsn} placeholder="-" InputProps={{ sx: { height: 24, fontSize: '0.75rem' } }} onChange={(e) => handleItemChange(index, 'hsn', e.target.value)} />
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <TextField fullWidth size="small" value={item.qty} placeholder="-" InputProps={{ sx: { height: 24, fontSize: '0.75rem' } }} onChange={(e) => handleItemChange(index, 'qty', e.target.value)} />
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <Select fullWidth size="small" displayEmpty value={item.unit || ''} sx={{ height: 24, fontSize: '0.75rem' }} onChange={(e) => handleItemChange(index, 'unit', e.target.value)}>
                      <MenuItem value=""><em>Select unit</em></MenuItem>
                      {units.map((unit) => <MenuItem key={unit.id} value={unit.name}>{unit.name}{unit.short_code ? ` (${unit.short_code})` : ''}</MenuItem>)}
                      {item.unit && !units.some((u) => u.name === item.unit) && (
                        <MenuItem value={item.unit}>{item.unit}</MenuItem>
                      )}
                    </Select>
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <TextField fullWidth size="small" value={item.price} placeholder="₹ -" InputProps={{ startAdornment: <Typography variant="caption">₹</Typography>, sx: { height: 24, fontSize: '0.75rem' } }} onChange={(e) => handleItemChange(index, 'price', e.target.value)} />
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <Select fullWidth size="small" value={item.taxType} sx={{ height: 24, fontSize: '0.75rem' }} onChange={(e) => handleItemChange(index, 'taxType', e.target.value)}>
                      <MenuItem value="Exclusive">Exclusive</MenuItem>
                      <MenuItem value="Inclusive">Inclusive</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <TextField fullWidth size="small" value={item.discount} placeholder="-" sx={{ flex: 1, minWidth: 50 }} InputProps={{ sx: { height: 24, fontSize: '0.75rem' } }} onChange={(e) => handleItemChange(index, 'discount', e.target.value)} />
                      <Select size="small" value={item.discountType} sx={{ width: 50, height: 24, fontSize: '0.75rem' }} onChange={(e) => handleItemChange(index, 'discountType', e.target.value)}>
                        <MenuItem value="%">%</MenuItem>
                        <MenuItem value="₹">₹</MenuItem>
                      </Select>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <TextField fullWidth size="small" value={item.taxableValue} placeholder="-" readOnly InputProps={{ sx: { height: 24, fontSize: '0.75rem' } }} />
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <Select fullWidth size="small" value={item.gst} displayEmpty sx={{ height: 24, fontSize: '0.75rem' }} onChange={(e) => handleItemChange(index, 'gst', e.target.value)}>
                      <MenuItem value="">No Tax</MenuItem>
                      {taxes.map((tax) => (
                        <MenuItem key={tax.id} value={String(tax.percentage)}>
                          {tax.title} ({tax.percentage}%)
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <TextField fullWidth size="small" value={item.totalAmt} placeholder="-" readOnly InputProps={{ sx: { height: 24, fontSize: '0.75rem' } }} />
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <IconButton size="small" color="error" onClick={() => handleDeleteItem(item.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Button 
          fullWidth 
          size="small" 
          variant="outlined" 
          startIcon={<AddIcon />}
          sx={{ mt: 1, borderStyle: 'dashed', height: 30 }}
          onClick={handleAddItem}
        >
          Add More Item
        </Button>

        {/* Summary Section */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ minWidth: 300 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption">Taxable Amount</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>₹ {totals.taxable.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption">GST Amount</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>₹ {totals.gst.toFixed(2)}</Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Total Amount</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>₹ {totals.total.toFixed(2)}</Typography>
            </Box>
            <Divider sx={{ my: 0.5, borderStyle: 'dashed' }} />
            {/* Show Other Charge/Discount Name and Amount if present */}
            {otherCharge > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption">
                    {otherChargeName ? otherChargeName : 'Other Charge'}
                  </Typography>
                  <IconButton size="small" sx={{ p: 0.1, color: 'error.main', ml: 0 }} onClick={() => { setOtherCharge(0); setOtherChargeName(''); }}>
                    <CloseIcon fontSize="inherit" style={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>+ ₹ {Number(otherCharge).toFixed(2)}</Typography>
              </Box>
            )}
            {otherDiscount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption">
                    {otherDiscountName ? otherDiscountName : 'Other Discount'}
                  </Typography>
                  <IconButton size="small" sx={{ p: 0.1, color: 'error.main', ml: 0 }} onClick={() => { setOtherDiscount(0); setOtherDiscountName(''); }}>
                    <CloseIcon fontSize="inherit" style={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>- ₹ {Number(otherDiscount).toFixed(2)}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Final Amount</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>₹ {totals.final.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{ height: 30, whiteSpace: 'nowrap', flex: 1 }}
                onClick={() => { setOtherType('charge'); setOtherDialogOpen(true); }}>
                Add Other Charge
              </Button>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{ height: 30, whiteSpace: 'nowrap', flex: 1 }}
                onClick={() => { setOtherType('discount'); setOtherDialogOpen(true); }}>
                Add Other Discount
              </Button>
              {/* Other Charge/Discount Dialog */}
              <Dialog open={otherDialogOpen} onClose={() => setOtherDialogOpen(false)} maxWidth="xs" fullWidth>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {otherType === 'charge' ? 'Add Other Charge' : 'Add Other Discount'}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    label={otherType === 'charge' ? 'Other Charge Amount' : 'Other Discount Amount'}
                    value={otherType === 'charge' ? otherCharge : otherDiscount}
                    onChange={e => {
                      const val = e.target.value;
                      if (otherType === 'charge') setOtherCharge(val);
                      else setOtherDiscount(val);
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label={otherType === 'charge' ? 'Other Charge Name/Description' : 'Other Discount Name/Description'}
                    value={otherType === 'charge' ? otherChargeName : otherDiscountName}
                    onChange={e => {
                      if (otherType === 'charge') setOtherChargeName(e.target.value);
                      else setOtherDiscountName(e.target.value);
                    }}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" size="small" onClick={() => setOtherDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button variant="contained" size="small" onClick={() => setOtherDialogOpen(false)}>
                      Save
                    </Button>
                  </Box>
                </Box>
              </Dialog>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Optional Fields */}
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="caption"
          sx={{
            bgcolor: '#ccc',
            p: 1,
            borderRadius: 1,
            fontWeight: 600,
            mb: 1,
            display: 'block',
          }}
        >
          Optional Fields
        </Typography>

        {/* Transportation Details and Other Details Side by Side */}
        <Box sx={{ display: 'flex', gap: 2, width: '100%', mb: 2 }}>
          {/* Transportation Details */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Accordion 
              expanded={optionalFieldsExpanded} 
              onChange={() => setOptionalFieldsExpanded(!optionalFieldsExpanded)}
              sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  🚚 TRANSPORTATION DETAILS
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2, flex: 1, overflow: 'auto' }}>
                <Box sx={{ width: '100%' }}>
                  {/* Transportation Mode */}
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                    Transportation Mode
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {['Road', 'Rail', 'Air', 'Ship/Road Cum Ship', 'Not Applicable'].map((mode) => (
                      <Button
                        key={mode}
                        size="small"
                        variant={transportDetails.transportation_mode === mode ? 'contained' : 'outlined'}
                        sx={{ height: 28, fontSize: '0.7rem' }}
                        onClick={() => setTransportDetails((prev) => ({ ...prev, transportation_mode: mode }))}
                      >
                        {mode}
                      </Button>
                    ))}
                  </Box>

                  {/* LR Number and LR Date */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        LR Number
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter LR Number"
                        size="small"
                        variant="outlined"
                        value={transportDetails.lr_number}
                        onChange={(e) => setTransportDetails((prev) => ({ ...prev, lr_number: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        LR Date
                      </Typography>
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        value={transportDetails.lr_date}
                        onChange={(e) => setTransportDetails((prev) => ({ ...prev, lr_date: e.target.value }))}
                      />
                    </Grid>
                  </Grid>

                  {/* Vehicle No. and Approximate Distance */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Vehicle No.
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter Vehicle No."
                        size="small"
                        variant="outlined"
                        value={transportDetails.vehicle_no}
                        onChange={(e) => setTransportDetails((prev) => ({ ...prev, vehicle_no: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Approximate Distance (in km)
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={transportDetails.distance_km}
                          onChange={(e) => setTransportDetails((prev) => ({ ...prev, distance_km: e.target.value }))}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Transporter Details */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#666' }}>
                      Transporter Details
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => setSelectTransporterDialogOpen(true)} sx={{ fontSize: '0.7rem', color: '#5c6bc0', p: 0.5, minWidth: 'auto' }} title="Select saved transporter">
                        <SwapIcon fontSize="small" />
                      </IconButton>
                      <Button size="small" variant="text" onClick={() => setTransporterDialogOpen(true)} sx={{ fontSize: '0.7rem', color: '#5c6bc0', p: 0, minWidth: 'auto' }}>
                        + Add New Transporter
                      </Button>
                    </Box>
                  </Box>
                  {(transportDetails.transporter_name || transportDetails.transporter_address) && (
                    <Box sx={{ bgcolor: '#f9f9f9', p: 1, borderRadius: 0.5, mb: 1 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.2 }}>
                        {transportDetails.transporter_name || '-'}{transportDetails.transporter_contact_no ? `, ${transportDetails.transporter_contact_no}` : ''}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#666', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {[transportDetails.transporter_address, transportDetails.transporter_city, transportDetails.transporter_state, transportDetails.transporter_pincode].filter(Boolean).join(', ') || '-'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Other Details */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Accordion 
              expanded={optionalFieldsExpanded} 
              onChange={() => setOptionalFieldsExpanded(!optionalFieldsExpanded)}
              sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  📋 OTHER DETAILS
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2, flex: 1, overflow: 'auto' }}>
                <Box sx={{ width: '100%' }}>
                  {/* Order Details: Place of Supply, Date of Supply, Supply Type */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Place of Supply
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter Place of Supply"
                        size="small"
                        variant="outlined"
                        value={otherDetails.place_of_supply}
                        onChange={(e) => setOtherDetails((prev) => ({ ...prev, place_of_supply: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Date of Supply
                      </Typography>
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        value={otherDetails.date_of_supply}
                        onChange={(e) => setOtherDetails((prev) => ({ ...prev, date_of_supply: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Supply Type
                      </Typography>
                      <Select
                        fullWidth
                        size="small"
                        value={otherDetails.supply_type}
                        onChange={(e) => setOtherDetails((prev) => ({ ...prev, supply_type: e.target.value }))}
                      >
                        <MenuItem value="B2B">B2B</MenuItem>
                        <MenuItem value="B2C">B2C</MenuItem>
                      </Select>
                    </Grid>
                  </Grid>

                  {/* Challan Number and E-Waybill Number */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Challan Number
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter Challan Number"
                        size="small"
                        variant="outlined"
                        value={otherDetails.challan_number}
                        onChange={(e) => setOtherDetails((prev) => ({ ...prev, challan_number: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        E-Waybill Number
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter E-Waybill Number"
                        size="small"
                        variant="outlined"
                        value={otherDetails.ewaybill_number}
                        onChange={(e) => setOtherDetails((prev) => ({ ...prev, ewaybill_number: e.target.value }))}
                      />
                    </Grid>
                  </Grid>

                  {/* Sales Person and Due Date */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Sales Person
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter Sales Person"
                        size="small"
                        variant="outlined"
                        value={otherDetails.sales_person}
                        onChange={(e) => setOtherDetails((prev) => ({ ...prev, sales_person: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                        Due Date
                      </Typography>
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        value={otherDetails.due_date}
                        onChange={(e) => setOtherDetails((prev) => ({ ...prev, due_date: e.target.value }))}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>

        {/* Document Copies */}
        <Paper sx={{ p: 2, mb: 1, bgcolor: '#f0f7ff', border: '2px solid #5c6bc0', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={originalForRecipient}
                  onChange={(e) =>
                    setOriginalForRecipient(e.target.checked)
                  }
                />
              }
              label={<Typography variant="caption">Original for Recipient</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={duplicateForTransporter}
                  onChange={(e) =>
                    setDuplicateForTransporter(e.target.checked)
                  }
                />
              }
              label={<Typography variant="caption">Duplicate for Transporter</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={triplicateForSupplier}
                  onChange={(e) =>
                    setTriplicateForSupplier(e.target.checked)
                  }
                />
              }
              label={<Typography variant="caption">Triplicate for Supplier</Typography>}
            />
          </Box>
        </Paper>

        {/* Bank Details, Terms & Conditions, Upload Signature - Side by Side */}
        <Box sx={{ display: 'flex', gap: 2, width: '100%', mb: 2 }}>
          {/* Bank Details */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper sx={{ p: 1.5, height: '100%', bgcolor: '#f5f5f5' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  🏦 BANK / PAYMENT DETAILS
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 1 }} />
              
              {/* Bank Logo Placeholder */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ width: 60, height: 30, bgcolor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, mr: 1 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600 }}>BANK</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedSeller?.bank_name || 'BANK NAME'}, {selectedSeller?.branch_name || '-'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ minWidth: 140 }}>
                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block' }}>A/C Holder Name</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.8rem' }}>{selectedSeller?.ac_holder_name || '-'}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 140 }}>
                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block' }}>A/C No.</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.8rem' }}>{selectedSeller?.ac_no || '-'}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 140 }}>
                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block' }}>IFSC Code</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.8rem' }}>{selectedSeller?.ifsc_code || '-'}</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block' }}>UPI ID</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.8rem' }}>{selectedSeller?.upi_id || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block' }}>UPI Number</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.8rem' }}>{selectedSeller?.upi_number || '-'}</Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Terms & Conditions */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper sx={{ p: 1.5, height: '100%', bgcolor: '#f5f5f5' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  📋 TERMS AND CONDITIONS
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton size="small" color="error" title="Remove">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="primary" title="Select Terms and Conditions">
                    <SearchIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="primary" title="Add New Terms and Conditions">
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 1 }} />
              
              <Box sx={{ pl: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  size="small"
                  value={termsConditions}
                  onChange={(e) => setTermsConditions(e.target.value)}
                  placeholder="Enter terms and conditions"
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                />
              </Box>
            </Paper>
          </Box>

          {/* Upload Signature */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper sx={{ p: 1.5, height: '100%', bgcolor: '#f5f5f5' }}>
              <Typography variant="caption" sx={{ textAlign: 'center', display: 'block', mb: 1, fontSize: '0.7rem', color: '#666' }}>
                Certified that the particular given above are true and correct,
              </Typography>
              <Typography variant="caption" sx={{ textAlign: 'center', display: 'block', mb: 1.5, fontSize: '0.75rem', fontWeight: 600 }}>
                For, {selectedSeller?.name || selectedSeller?.company || 'SELLER NAME'}
              </Typography>
              
              {/* Signature Box */}
              <Box sx={{ 
                border: '2px dashed #ccc', 
                borderRadius: 1, 
                minHeight: 100, 
                mb: 1,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                p: 1,
                bgcolor: '#fff'
              }}>
                {signStampImage ? (
                  <Box component="img" src={signStampImage} alt="Uploaded stamp" sx={{ maxWidth: '100%', maxHeight: 90, objectFit: 'contain' }} />
                ) : (
                  <Typography variant="caption" sx={{ color: '#888' }}>No stamp uploaded</Typography>
                )}
              </Box>
              
              <Button 
                component="label"
                size="small" 
                variant="text" 
                fullWidth 
                disabled={stampUploading}
                sx={{ fontSize: '0.7rem', color: '#5c6bc0', mb: 1 }}
              >
                {stampUploading ? 'Uploading...' : 'Upload Stamp'}
                <input hidden accept="image/jpeg,image/png,image/webp" type="file" onChange={handleStampUpload} />
              </Button>
              {signStampImage && (
                <Button size="small" variant="text" startIcon={<DeleteIcon />} onClick={() => setSignStampImage('')} sx={{ display: 'block', mx: 'auto', mb: 1, fontSize: '0.7rem' }}>
                  Clear Stamp
                </Button>
              )}
              
              <Divider sx={{ mb: 0.5 }} />
              
              <Typography variant="caption" sx={{ textAlign: 'center', display: 'block', fontSize: '0.7rem', color: '#666' }}>
                Authorised Signatory
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Seller Selection Dialog */}
      <Dialog
        open={sellerDialogOpen}
        onClose={() => setSellerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Select Seller
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, company, GSTIN"
            value={vendorSearch}
            onChange={(e) => setVendorSearch(e.target.value)}
            sx={{ mb: 1 }}
          />
          <List>
            {filteredVendors.map((seller) => (
              <ListItemButton
                key={seller.id}
                onClick={() => {
                  setSelectedSeller(seller);
                  setSellerDialogOpen(false);
                }}
              >
                <ListItemText
                  primary={seller.name || seller.company}
                  secondary={`${seller.city || ''} ${seller.city && seller.gstin ? '-' : ''} ${seller.gstin || ''}`}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Dialog>

      {/* Consignee Address Dialog */}
      <Dialog
        open={consigneeDialogOpen}
        onClose={() => {
          setConsigneeDialogOpen(false);
          setSellerOption('show-consignee');
        }}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Add Consignee Address
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company/Person Name"
                size="small"
                variant="outlined"
                value={consigneeForm.name}
                onChange={(e)=>setConsigneeForm(f=>({...f,name:e.target.value}))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                size="small"
                variant="outlined"
                multiline
                rows={2}
                value={consigneeForm.address}
                onChange={(e)=>setConsigneeForm(f=>({...f,address:e.target.value}))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="City"
                size="small"
                variant="outlined"
                value={consigneeForm.city}
                onChange={(e)=>setConsigneeForm(f=>({...f,city:e.target.value}))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="State"
                size="small"
                variant="outlined"
                value={consigneeForm.state}
                onChange={(e)=>setConsigneeForm(f=>({...f,state:e.target.value}))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Pincode"
                size="small"
                variant="outlined"
                value={consigneeForm.pincode}
                onChange={(e)=>setConsigneeForm(f=>({...f,pincode:e.target.value}))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="GSTIN (Optional)"
                size="small"
                variant="outlined"
                value={consigneeForm.gstin}
                onChange={(e)=>setConsigneeForm(f=>({...f,gstin:e.target.value}))}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setConsigneeDialogOpen(false);
                setSellerOption('show-consignee');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={async () => {
                try {
                  const payload = {
                    name: consigneeForm.name || '',
                    company: null,
                    address: consigneeForm.address || null,
                    city: consigneeForm.city || null,
                    state: consigneeForm.state || null,
                    pincode: consigneeForm.pincode || null,
                    gstin: consigneeForm.gstin || null,
                    contact_no: null,
                  };
                  const created = await createConsignee(payload);
                  const id = created?.id || null;
                  setConsigneeDetails({ id, ...payload });
                } catch (e) {
                  console.error('create consignee (different address) error', e);
                  setConsigneeDetails(consigneeForm);
                } finally {
                  setConsigneeDialogOpen(false);
                  setSellerOption('add-consignee');
                }
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Consignee Selection Dialog */}
      <Dialog
        open={consigneeSelectDialogOpen}
        onClose={() => setConsigneeSelectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Select Consignee
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, company, GSTIN"
            value={consigneeSearch}
            onChange={(e) => setConsigneeSearch(e.target.value)}
            sx={{ mb: 1 }}
          />
          <List>
            {(consignees || []).map((c) => (
              <ListItemButton
                key={c.id}
                onClick={() => {
                  setConsigneeDetails({
                    id: c.id,
                    name: c.name || c.company || '',
                    address: c.address || '',
                    city: c.city || '',
                    state: c.state || '',
                    pincode: c.pincode || '',
                    gstin: c.gstin || '',
                  });
                  setSellerOption('add-consignee');
                  setConsigneeSelectDialogOpen(false);
                }}
              >
                <ListItemText
                  primary={c.name || c.company}
                  secondary={`${c.city || ''} ${c.city && c.gstin ? '-' : ''} ${c.gstin || ''}`}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Dialog>

      {/* Select Saved Transporter Dialog */}
      <Dialog
        open={selectTransporterDialogOpen}
        onClose={() => setSelectTransporterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Select Transporter
          </Typography>
          {savedTransporters.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#666', py: 2 }}>
              No saved transporters found. Add a new one.
            </Typography>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {savedTransporters.map((transporter, idx) => (
                <ListItemButton
                  key={idx}
                  onClick={() => handleSelectTransporter(transporter)}
                  sx={{ borderBottom: '1px solid #eee', py: 1.5 }}
                >
                  <Box sx={{ width: '100%' }}>
                    <ListItemText
                      primary={transporter.transporter_name || '-'}
                      secondary={`${transporter.transporter_address || '-'}, ${transporter.transporter_city || '-'}`}
                    />
                    <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }}>
                      {transporter.transporter_contact_no || '-'}
                    </Typography>
                  </Box>
                </ListItemButton>
              ))}
            </List>
          )}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSelectTransporterDialogOpen(false)}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Transporter Details Dialog */}
      <Dialog
        open={transporterDialogOpen}
        onClose={() => setTransporterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Add Transporter Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Transporter Name"
                size="small"
                variant="outlined"
                value={transportDetails.transporter_name || selectedSeller?.name || ''}
                onChange={(e) => setTransportDetails((prev) => ({ ...prev, transporter_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                size="small"
                variant="outlined"
                multiline
                rows={2}
                value={transportDetails.transporter_address || selectedSeller?.address || ''}
                onChange={(e) => setTransportDetails((prev) => ({ ...prev, transporter_address: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="City"
                size="small"
                variant="outlined"
                value={transportDetails.transporter_city || selectedSeller?.city || ''}
                onChange={(e) => setTransportDetails((prev) => ({ ...prev, transporter_city: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="State"
                size="small"
                variant="outlined"
                value={transportDetails.transporter_state || selectedSeller?.state || ''}
                onChange={(e) => setTransportDetails((prev) => ({ ...prev, transporter_state: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Pincode"
                size="small"
                variant="outlined"
                value={transportDetails.transporter_pincode || selectedSeller?.pincode || ''}
                onChange={(e) => setTransportDetails((prev) => ({ ...prev, transporter_pincode: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="GST Number"
                size="small"
                variant="outlined"
                value={transportDetails.transporter_gstin || selectedSeller?.gstin || ''}
                onChange={(e) => setTransportDetails((prev) => ({ ...prev, transporter_gstin: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Number"
                size="small"
                variant="outlined"
                value={transportDetails.transporter_contact_no}
                onChange={(e) => setTransportDetails((prev) => ({ ...prev, transporter_contact_no: e.target.value }))}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setTransporterDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                setTransporterDialogOpen(false);
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Scan Invoice Image Dialog */}
      <Dialog
        open={scanDialogOpen}
        onClose={closeScanDialog}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <DocumentScannerIcon /> Scan Invoice Image
            </Typography>
            <IconButton size="small" onClick={closeScanDialog}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {scanError && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setScanError('')}>
              {scanError}
            </Alert>
          )}

          {scanDebug && (
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#888', fontSize: '0.68rem', fontFamily: 'monospace' }}>
              🔧 {scanDebug}
            </Typography>
          )}

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={scanEnhance}
                onChange={(e) => handleEnhanceToggle(e.target.checked)}
                disabled={scanning}
              />
            }
            label={<Typography variant="caption" sx={{ fontSize: '0.72rem' }}>✨ Enhance unclear image before reading (upscale + contrast + sharpen)</Typography>}
            sx={{ mb: 1 }}
          />

          {!scanImagePreview && (
            <Box
              onClick={() => scanFileInputRef.current?.click()}
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                py: 6,
                textAlign: 'center',
                cursor: scanning ? 'not-allowed' : 'pointer',
                bgcolor: '#fafafa',
                '&:hover': { borderColor: '#5c6bc0', bgcolor: '#f5f5ff' },
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: '#999', mb: 1 }} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                Click to upload an invoice image, screenshot or PDF
              </Typography>
              <Typography variant="caption" sx={{ color: '#999' }}>
                JPG, PNG, WebP, PDF supported
              </Typography>
            </Box>
          )}

          {scanImagePreview && (
            <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ maxWidth: 280 }}>
                <Box
                  component="img"
                  src={scanImagePreview}
                  alt="Invoice preview"
                  sx={{ maxWidth: 280, maxHeight: 320, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 1 }}
                />
                {scanPages.length > 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                    <IconButton size="small" onClick={() => changeScanPage(-1)} disabled={scanPageIndex === 0}>
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ color: '#666', whiteSpace: 'nowrap' }}>
                      Page {scanPageIndex + 1} / {scanPages.length}
                    </Typography>
                    <IconButton size="small" onClick={() => changeScanPage(1)} disabled={scanPageIndex === scanPages.length - 1}>
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                {scanPages.length === 1 && scanPageIndex === 0 && scanPages[0] && (
                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5, color: '#888' }}>
                    PDF · 1 page
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 240 }}>
                {scanning && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666' }}>
                      {scanStatus || 'Processing...'} {scanProgress > 0 ? `(${scanProgress}%)` : ''}
                    </Typography>
                    <LinearProgress variant={scanProgress ? 'determinate' : 'indeterminate'} value={scanProgress} />
                  </Box>
                )}
                {!scanning && scanParsedRows.length > 0 && (
                  <>
                    <Typography variant="caption" sx={{ color: '#2e7d32', display: 'block', mb: 1 }}>
                      ✓ {scanParsedRows.length} item(s) detected{scanMatched > 0 ? `, ${scanMatched} matched to existing products` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="contained" color="primary" onClick={applyScanResults}>
                        Apply to Invoice
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        component="label"
                        sx={{ height: 30 }}
                      >
                        Choose Another
                        <input hidden accept="image/*,application/pdf,.pdf" type="file" onChange={handleScanFileSelect} />
                      </Button>
                      <Button size="small" variant="text" color="error" onClick={() => { setScanImagePreview(''); setScanPages([]); setScanPageIndex(0); }}>
                        Remove
                      </Button>
                    </Box>
                  </>
                )}
                {!scanning && !scanParsedRows.length && !scanError && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button size="small" variant="outlined" component="label" sx={{ height: 30 }}>
                      Re-scan File
                      <input hidden accept="image/*,application/pdf,.pdf" type="file" onChange={handleScanFileSelect} />
                    </Button>
                    <Button size="small" variant="text" color="error" onClick={() => { setScanImagePreview(''); setScanPages([]); setScanPageIndex(0); }}>
                      Remove
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {scanning && scanParsedRows.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {scanParsedRows.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Detected Items (verify before applying)
              </Typography>

              {/* Column mapping: invoice columns -> purchase columns */}
              {scanTable?.columns?.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1, mb: 1.5, bgcolor: '#fafafa' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#5c6bc0' }}>
                    🧭 Map Invoice Columns (columns detected from your invoice image)
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#888', fontSize: '0.68rem' }}>
                    The invoice columns may be shuffled or use short forms. Use the dropdowns to tell the app which invoice column goes into which purchase column.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {scanTable.columns.map((col) => (
                      <Box
                        key={col.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          bgcolor: '#fff',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, color: '#333', whiteSpace: 'nowrap', fontSize: '0.72rem' }}
                        >
                          {col.text || col.id}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#aaa', fontSize: '0.65rem' }}>→</Typography>
                        <Select
                          size="small"
                          value={scanMapping[col.id] || ''}
                          onChange={(e) => handleMappingChange(col.id, e.target.value)}
                          sx={{ height: 26, fontSize: '0.72rem', minWidth: 110 }}
                        >
                          {TARGET_FIELDS.map((f) => (
                            <MenuItem key={f.value || 'skip'} value={f.value}>
                              {f.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}

              <TableContainer sx={{ maxHeight: 260, border: '1px solid #eee', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ p: 0.5, minWidth: 24 }}>#</TableCell>
                      <TableCell sx={{ p: 0.5, minWidth: 160 }}>Item Name</TableCell>
                      <TableCell sx={{ p: 0.5, minWidth: 70 }}>MRP</TableCell>
                      <TableCell sx={{ p: 0.5, minWidth: 70 }}>HSN</TableCell>
                      <TableCell sx={{ p: 0.5, minWidth: 50 }}>Qty</TableCell>
                      <TableCell sx={{ p: 0.5, minWidth: 50 }}>Unit</TableCell>
                      <TableCell sx={{ p: 0.5, minWidth: 70 }}>Price</TableCell>
                      <TableCell sx={{ p: 0.5, minWidth: 80 }}>Tax Type</TableCell>
                      <TableCell sx={{ p: 0.5, minWidth: 70 }}>Discount</TableCell>
                      <TableCell sx={{ p: 0.5, minWidth: 90 }}>Taxable Value</TableCell>
                      <TableCell sx={{ p: 0.5, minWidth: 50 }}>GST</TableCell>
                      <TableCell sx={{ p: 0.5, minWidth: 90 }}>Total Amt</TableCell>
                      <TableCell sx={{ p: 0.5, minWidth: 90 }}>Matched</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scanParsedRows.map((row, idx) => {
                      const qtyN = Number(row.qty) || 0;
                      const priceN = Number(row.price) || 0;
                      const gstN = Number(row.gst) || 0;
                      const taxableVal = qtyN && priceN ? (qtyN * priceN).toFixed(2) : '';
                      const totalN = Number(row.totalAmt) || (qtyN && priceN ? qtyN * priceN * (1 + gstN / 100) : 0);
                      const cellSx = { p: 0.3, verticalAlign: 'top' };
                      return (
                        <TableRow key={idx}>
                          <TableCell sx={cellSx}>{idx + 1}</TableCell>
                          <TableCell sx={cellSx}>
                            <TextField
                              fullWidth
                              size="small"
                              value={row.itemName || ''}
                              onChange={(e) => updateScanRow(idx, 'itemName', e.target.value)}
                              placeholder="-"
                              InputProps={{ sx: { height: 24, fontSize: '0.72rem' } }}
                            />
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <TextField
                              size="small"
                              value={row.mrp || ''}
                              onChange={(e) => updateScanRow(idx, 'mrp', e.target.value)}
                              placeholder="-"
                              InputProps={{ sx: { height: 24, fontSize: '0.72rem', minWidth: 46 } }}
                            />
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <TextField
                              size="small"
                              value={row.hsn || ''}
                              onChange={(e) => updateScanRow(idx, 'hsn', e.target.value)}
                              placeholder="-"
                              InputProps={{ sx: { height: 24, fontSize: '0.72rem', minWidth: 46 } }}
                            />
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <TextField
                              size="small"
                              value={row.qty || ''}
                              onChange={(e) => updateScanRow(idx, 'qty', e.target.value)}
                              placeholder="-"
                              InputProps={{ sx: { height: 24, fontSize: '0.72rem', minWidth: 40 } }}
                            />
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <TextField
                              size="small"
                              value={row.unit || ''}
                              onChange={(e) => updateScanRow(idx, 'unit', e.target.value)}
                              placeholder="-"
                              InputProps={{ sx: { height: 24, fontSize: '0.72rem', minWidth: 40 } }}
                            />
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <TextField
                              size="small"
                              value={row.price || ''}
                              onChange={(e) => updateScanRow(idx, 'price', e.target.value)}
                              placeholder="-"
                              InputProps={{ sx: { height: 24, fontSize: '0.72rem', minWidth: 50 } }}
                            />
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <Typography variant="caption" sx={{ fontSize: '0.72rem', color: '#888' }}>
                              Exclusive
                            </Typography>
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <TextField
                              size="small"
                              value={row.discount || ''}
                              onChange={(e) => updateScanRow(idx, 'discount', e.target.value)}
                              placeholder="-"
                              InputProps={{ sx: { height: 24, fontSize: '0.72rem', minWidth: 40 } }}
                            />
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>
                              {taxableVal ? `₹${taxableVal}` : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <TextField
                              size="small"
                              value={row.gst || ''}
                              onChange={(e) => updateScanRow(idx, 'gst', e.target.value)}
                              placeholder="-"
                              InputProps={{ sx: { height: 24, fontSize: '0.72rem', minWidth: 40 } }}
                            />
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <TextField
                              size="small"
                              value={row.totalAmt || ''}
                              onChange={(e) => updateScanRow(idx, 'totalAmt', e.target.value)}
                              placeholder={totalN ? `₹${totalN.toFixed(2)}` : '-'}
                              InputProps={{ sx: { height: 24, fontSize: '0.72rem', minWidth: 55 } }}
                            />
                          </TableCell>
                          <TableCell sx={cellSx}>
                            {row.match ? (
                              <Tooltip title={row.match.product?.name || ''}>
                                <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }}>
                                  ✓ Matched
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem' }}>New</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {scanRawText && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Raw OCR Text
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={5}
                    size="small"
                    value={scanRawText}
                    onChange={(e) => setScanRawText(e.target.value)}
                    placeholder="Raw extracted text..."
                  />
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.5 }}>
                <Button size="small" variant="outlined" onClick={closeScanDialog}>
                  Cancel
                </Button>
                <Button size="small" variant="contained" color="primary" onClick={applyScanResults} disabled={matchingProducts}>
                  {matchingProducts ? 'Matching...' : 'Apply to Invoice'}
                </Button>
              </Box>
            </Box>
          )}

          <input
            ref={scanFileInputRef}
            hidden
            accept="image/*,application/pdf,.pdf"
            type="file"
            onChange={handleScanFileSelect}
          />
        </Box>
      </Dialog>
    </Box>
  );
}

